import { ADMIN_ORG_ID } from '@/apis/users-apis';
import { APP_ROUTES, ModuleInfo } from '@/constants/app-constants';
import useOrg from '@/hooks/useOrg';
import { OrganizationAndPartnerSearch } from '@/sidebar/org-and-partner-search';
import { Flex, HStack, Icon, Image, Separator, Text } from '@chakra-ui/react';
import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { useEffect, useState } from 'react';
import { FiLogOut } from 'react-icons/fi';
import { Link, matchRoutes, useLocation } from 'react-router-dom';
import { DoubleArrowLeftIcon, DoubleArrowRightIcon, KintsugiLogoLight, WarningIcon } from '../components/icons';
import { Button } from '../components/ui/button';
import { LinkButton, LinkButtonProps } from '../components/ui/link-button';
import { isOrgHealthCheckEnabled } from '@/utils/feature-flags';
import { resolveOrgRoute, resolvePartnerRoute } from '@/utils/routes';
import { Avatar, Menu, Tooltip } from '@kintsugi/ui';
import logoSm from './components/logo.sm.svg';
import usePartner from '@/hooks/usePartner';

type NavItemProps = {
  href: string;
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  warningMessage?: string;
} & LinkButtonProps;

const NavItem = ({
  title,
  href,
  icon: providedIcon,
  isExpanded = true,
  children,
  warningMessage,
  ...rest
}: NavItemProps) => {
  return (
    <Tooltip
      content={title}
      showArrow
      disabled={isExpanded}
      positioning={{ placement: 'right-start', offset: { crossAxis: 4 } }}
    >
      <LinkButton size="sm" variant={'nav' as any} asChild gap="3" {...rest}>
        <Link to={href}>
          {providedIcon && <Icon boxSize="24px">{providedIcon}</Icon>}
          <HStack gap={1.5}>
            {isExpanded ? children : null}
            {isExpanded && warningMessage && (
              <Tooltip content={warningMessage} positioning={{ placement: 'bottom-end' }}>
                <WarningIcon color="orange.500" width="5" height="5" />
              </Tooltip>
            )}
          </HStack>
        </Link>
      </LinkButton>
    </Tooltip>
  );
};

type SidebarProps = {
  hideToggleBtn?: boolean;
  isExpanded?: boolean;
  onSidebarExpand?: () => void;
  onClose?: () => void;
};
export const Sidebar = ({ hideToggleBtn = true, isExpanded = true, onSidebarExpand, onClose }: SidebarProps) => {
  const { user, accessHelper } = useAuthInfo();
  const userAvatarSrc = user?.pictureUrl || '';
  const userAvatarName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || undefined;
  const location = useLocation();
  const propelLogout = useLogoutFunction();
  const org = useOrg();
  const partner = usePartner();
  const isOrgSelected = !!org?.id;
  const isPartnerSelected = !!partner?.id;

  const active = APP_ROUTES.find(({ routesToMatch }: ModuleInfo) => !!matchRoutes(routesToMatch, location));
  const [activeButton, setActiveButton] = useState<string | undefined>(active?.name);

  useEffect(() => {
    const newActive = APP_ROUTES.find(({ routesToMatch }: ModuleInfo) => !!matchRoutes(routesToMatch, location));
    setActiveButton(newActive?.name);
  }, [location]);

  const healthCheckEnabled = isOrgHealthCheckEnabled();

  const groupedRoutes = APP_ROUTES.reduce(
    (acc, route) => {
      // Routes without a sideBarSectionName are detail-only routes (e.g. a
      // reseller detail page) kept in APP_ROUTES purely for header labels and
      // active-state matching. They must not render as sidebar nav items.
      if (!route.sideBarSectionName) {
        return acc;
      }
      // Permission-gated nav items stay hidden unless accessHelper confirms.
      if (route.permission && !accessHelper?.hasPermission(ADMIN_ORG_ID, route.permission)) {
        return acc;
      }
      // KA-1511 rollout gate: hide the Kintsugi Tasks nav item until enabled.
      if (route.href === '/kintsugi-tasks' && !healthCheckEnabled) {
        return acc;
      }
      const sectionName = route.sideBarSectionName;
      if (!acc[sectionName]) {
        acc[sectionName] = [];
      }
      acc[sectionName].push(route);
      return acc;
    },
    {} as Record<string, ModuleInfo[]>
  );

  const isOrgOrPartnerSelected = isOrgSelected || isPartnerSelected;

  const logoutFunction = () => {
    localStorage.clear();
    propelLogout(false);
  };

  const handleNavClick = (item: ModuleInfo) => {
    setActiveButton(item.name);
    onClose?.();
  };

  const renderLogo = isExpanded ? (
    <Flex gap={1} justify={'center'} align={'center'}>
      <KintsugiLogoLight height="30px" width="124px" />
    </Flex>
  ) : (
    <Image src={logoSm} height={'20px'} width="20px" onClick={onSidebarExpand} cursor={'pointer'} />
  );

  const renderOrganizationsAndPartnersSection = (moduleInfo: ModuleInfo) => {
    const isPartnerRoute = moduleInfo.href.includes('/partners');
    const isOrganizationsAndPartnersSection = moduleInfo.sideBarSectionName === 'Organizations & Partners';
    let targetHref = moduleInfo.href;

    if (isOrganizationsAndPartnersSection && isPartnerSelected && !isPartnerRoute) {
      return null;
    }

    if (isOrganizationsAndPartnersSection && isOrgSelected && isPartnerRoute) {
      return null;
    }

    if (isOrganizationsAndPartnersSection && partner && isPartnerRoute) {
      targetHref = resolvePartnerRoute(moduleInfo.href, partner.id);
    } else if (isOrganizationsAndPartnersSection && isOrgSelected) {
      targetHref = resolveOrgRoute(moduleInfo.href, org.id);
    }

    return (
      <NavItem
        key={moduleInfo.name}
        title={moduleInfo.name}
        data-onboarding={moduleInfo.name}
        data-expanded={isExpanded}
        data-active={activeButton === moduleInfo.name || undefined}
        icon={moduleInfo.icon}
        onClick={() => handleNavClick(moduleInfo)}
        href={targetHref}
        isExpanded={isExpanded}
      >
        <Text color={'white'} lineClamp={1}>
          {moduleInfo.name}
        </Text>
      </NavItem>
    );
  };

  return (
    <Flex
      as={'nav'}
      data-onboarding="sidebar"
      h={'100%'}
      bgColor="colorPalette.fg"
      flexDir="column"
      justifyContent="space-between"
      py={'16px'}
      overflowY="auto"
      overflowX={'hidden'}
    >
      <Flex px="5%" flexDirection="column">
        <Flex justify={isExpanded ? 'space-between' : 'center'} align={'center'} mb={2}>
          {renderLogo}
          {!hideToggleBtn && (
            <Button variant="plain" onClick={onSidebarExpand} px="0" minWidth="24px">
              <Icon color="neonGreen.500">{isExpanded ? <DoubleArrowLeftIcon /> : <DoubleArrowRightIcon />}</Icon>
            </Button>
          )}
        </Flex>
        <Flex flexDirection={'column'} justifyContent={'space-between'} mt={5}>
          {Object.entries(groupedRoutes).map(([sectionName, routes], index) => (
            <Flex key={sectionName} flexDirection={'column'} justifyContent={'space-between'}>
              {isExpanded && (
                <Text fontWeight={'500'} color={'white'} fontSize={'14px'} mb={3}>
                  {sectionName}
                </Text>
              )}
              {sectionName === 'Organizations & Partners' && <OrganizationAndPartnerSearch placeholder={'Search'} />}
              {/* Hide Organizations section routes if no organization is selected */}
              {sectionName !== 'Organizations & Partners' || isOrgOrPartnerSelected ? (
                routes.map(renderOrganizationsAndPartnersSection)
              ) : (
                <Flex justifyContent={'center'} minH={'10rem'} mt={4}>
                  <Text fontSize="sm" color={'white'} lineClamp={1}>
                    Please Select Organization or Partner
                  </Text>
                </Flex>
              )}
              {index !== Object.entries(groupedRoutes).length - 1 && (
                <Separator colorPalette={'gray'} size="sm" my={5} />
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Flex px="5%" flexDirection={'column'}>
        <Menu.Root positioning={{ placement: 'top' }}>
          <Menu.Trigger asChild my={3} px={2.5}>
            <Button
              justifyContent="start"
              alignSelf={isExpanded ? 'start' : 'center'}
              variant="plain"
              p="0"
              width={!isExpanded ? '45px' : undefined}
              minW="45px"
              asChild
            >
              <Flex gap={3}>
                <Avatar size="sm" src={userAvatarSrc} name={userAvatarName} />
                {isExpanded && (
                  <Text lineClamp={1} color={'white'}>
                    {user?.firstName + ' ' + user?.lastName}
                  </Text>
                )}
              </Flex>
            </Button>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Item value="logout" onClick={logoutFunction} startIcon={<FiLogOut />}>
              Logout
            </Menu.Item>
          </Menu.Content>
        </Menu.Root>
      </Flex>
    </Flex>
  );
};
